import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Eye,
  ExternalLink,
  FileBarChart,
  FileText,
  Mail,
  MessageCircle,
  MessagesSquare,
  Paperclip,
  Phone,
  Plus,
  Send,
  Settings2,
  Star,
  StickyNote,
  Trash2,
  Users,
  WandSparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../services/erpApi";
import { AVATAR_BG, getInitials, ODOO, parseExtrasFromDescription, serializeExtrasToDescription } from "./crmV2Helpers";

const pageFont = '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif';

// Champs réellement supportés par PUT /api/crm-leads/:id (ROUTES.md:1146)
// partner_name/project_id/properties/category etc. n'existent pas côté API → local-only
const CRM_UPDATE_FIELDS = new Set([
  "name",
  "type",
  "stage_id",
  "company",
  "partner_id",
  "email_from",
  "phone",
  "mobile",
  "user_id",
  "team_id",
  "priority",
  "expected_revenue",
  "plan_revenue",
  "probability",
  "source_id",
  "medium_id",
  "campaign_id",
  "date_open",
  "date_deadline",
  "description",
  "tag_ids",
  "group_ids",
]);

// Champs maquette Odoo → désormais persistés via `description` (JSON embarqué)
// jamais envoyés tels quels à PUT, mais sérialisés dans description via helpers
const LOCAL_ONLY_FIELDS = new Set([
  "partner_name",
  "project_id",
  "project_name",
  "category",
  "type_detail",
  "vehicle_type",
  "plate_number",
  "contact_name",
  "contact_title",
  "street",
  "address",
  "job_position",
  "city",
  "state",
  "zip",
  "country",
  "website",
  "language",
  "campaign",
  "medium",
  "source",
  "referred_by",
  "properties",
]);

// Clés backend réservées (lecture seule) : jamais injectées dans un champ texte éditable.
// `vehicules` = array d'objets, `vehicules_display` = texte pré-formaté backend.
const READONLY_VEHICLE_KEYS = new Set(["vehicules", "vehicules_display", "vehicles", "vehicles_display"]);

const isVehicleKey = (name) => READONLY_VEHICLE_KEYS.has(String(name || "").toLowerCase());

// Ne jamais transformer un objet/array brut en champ texte : on exclut les clés
// véhicules et on stringify en sécurité tout autre objet accidentel.
const normalizeProperties = (value) => {
  const list = Array.isArray(value)
    ? value
    : Object.entries(value || {}).map(([name, propertyValue]) => ({ name, type: "Text", value: propertyValue }));
  return list
    .filter((p) => !isVehicleKey(p?.name))
    .map((p) => {
      const v = p?.value;
      if (v !== null && typeof v === "object") {
        try {
          return { name: p.name, type: "Text", value: JSON.stringify(v) };
        } catch {
          return { name: p.name, type: "Text", value: "" };
        }
      }
      return p;
    });
};

// Extrait l'affichage véhicules : priorité à vehicules_display, fallback tableau
// construit depuis properties.vehicules (modèle / immatriculation / montant / statut).
const extractVehiclesInfo = (leadProps, extrasProps) => {
  const sources = [leadProps, extrasProps].filter((s) => s && typeof s === "object" && !Array.isArray(s));
  let display = "";
  let list = [];
  for (const src of sources) {
    if (!display) display = src.vehicules_display ?? src.vehicles_display ?? src.vehicule_display ?? "";
    if (!list.length) {
      const raw = src.vehicules ?? src.vehicles ?? src.vehicule ?? [];
      if (Array.isArray(raw)) list = raw;
    }
  }
  // Cas où properties lui-même est un array [{name:"vehicules", value:[...]}]
  const arraySources = [leadProps, extrasProps].filter(Array.isArray);
  for (const arr of arraySources) {
    for (const p of arr) {
      if (isVehicleKey(p?.name)) {
        if (!display && typeof p?.value === "string") display = p.value;
        if (!list.length && Array.isArray(p?.value)) list = p.value;
      }
    }
  }
  // Normalise chaque véhicule vers {modele, immatriculation, montant, statut}
  list = (Array.isArray(list) ? list : []).map((v) => {
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      return {
        modele: v.modele ?? v.model ?? v.model_name ?? v.name ?? v.vehicule ?? "",
        immatriculation: v.immatriculation ?? v.plate ?? v.plate_number ?? v.immat ?? "",
        montant: v.montant ?? v.amount ?? v.price ?? v.expected_revenue ?? "",
        statut: v.statut ?? v.status ?? v.state ?? "",
      };
    }
    return { modele: String(v ?? ""), immatriculation: "", montant: "", statut: "" };
  });
  return { display: String(display || ""), list };
};

// Construit le payload extras à sérialiser dans description
const buildExtrasObject = (extraFields, extraInfo, properties) => {
  const extras = {};
  if (extraFields.category) extras.category = extraFields.category;
  if (extraFields.type) extras.type_detail = extraFields.type;
  if (extraFields.vehicle_type) extras.vehicle_type = extraFields.vehicle_type;
  if (extraFields.plate_number) extras.plate_number = extraFields.plate_number;
  // extraInfo — seules les clés local-only y sont persistées
  const localKeys = ["company_name", "contact_name", "contact_title", "address", "job_position", "city", "state", "zip", "country", "website", "language", "referred_by", "tracking_company", "tracking_team"];
  for (const k of localKeys) {
    if (extraInfo[k]) extras[k] = extraInfo[k];
  }
  if (Array.isArray(properties) && properties.length) extras.properties = properties;
  return extras;
};

const sanitizePatch = (patch) => {
  const out = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === "" || v === null || v === undefined) {
      // tag_ids / group_ids vides = [], autres = undefined (ignoré côté API)
      if (k === "tag_ids" || k === "group_ids") out[k] = [];
      continue;
    }
    if (["stage_id", "user_id", "team_id", "partner_id", "source_id", "medium_id", "campaign_id"].includes(k)) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n)) out[k] = n;
    } else if (["expected_revenue", "plan_revenue", "probability"].includes(k)) {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) out[k] = n;
    } else if (k === "priority") {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n)) out[k] = Math.max(0, Math.min(3, n));
    } else if (k === "tag_ids" || k === "group_ids") {
      out[k] = Array.isArray(v) ? v.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n)) : [];
    } else {
      out[k] = v;
    }
  }
  return out;
};

const buttonStyle = {
  border: `1px solid ${ODOO.border}`,
  background: "#fff",
  color: ODOO.text,
  borderRadius: 2,
  fontFamily: pageFont,
  cursor: "pointer",
};

const normalize = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value) && "data" in value) return value.data;
  return value;
};

const formatMoney = (value) => Number(value || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const relativeTime = (value) => {
  if (!value) return "";
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};

const IconButton = ({ label, children, onClick, disabled }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    style={{
      ...buttonStyle,
      width: 28,
      height: 27,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: disabled ? "#adb5bd" : ODOO.muted,
      cursor: disabled ? "default" : "pointer",
    }}
  >
    {children}
  </button>
);

const StageBar = ({ stages, currentStageId, onSelect }) => {
  const activeId = String(currentStageId || "");
  return (
    <div className="crm-stage-bar">
      {stages.map((stage, index) => {
        const active = String(stage.id) === activeId;
        const first = index === 0;
        const last = index === stages.length - 1;
        let clipPath = "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)";
        if (first) clipPath = "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)";
        if (last) clipPath = "polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)";
        if (stages.length === 1) clipPath = "none";
        return (
          <button
            key={stage.id}
            type="button"
            title={stage.name}
            onClick={() => onSelect && onSelect(stage)}
            style={{
              flex: "1 1 0",
              minWidth: 62,
              border: 0,
              marginLeft: first ? 0 : -10,
              padding: first ? "0 8px" : "0 8px 0 18px",
              clipPath,
              background: active ? "#212529" : "#e9ecef",
              color: active ? "#fff" : "#495057",
              fontFamily: pageFont,
              fontSize: 9,
              fontWeight: active ? 700 : 500,
              lineHeight: 1,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "pointer",
            }}
          >
            {stage.name}
          </button>
        );
      })}
    </div>
  );
};

const SmartButton = ({ icon: Icon, label, count, onClick }) => (
  <button type="button" onClick={onClick} className="crm-smart-button">
    <Icon size={13} />
    <span>{label}</span>
    {count !== undefined ? <strong>{count}</strong> : null}
  </button>
);

const Field = ({ label, children }) => (
  <div className="crm-field">
    <span>{label}</span>
    <div>{children}</div>
  </div>
);

const Input = ({ value, placeholder, onChange, type = "text" }) => (
  <input
    type={type}
    value={value || ""}
    placeholder={placeholder}
    onChange={(event) => onChange && onChange(event.target.value)}
    className="crm-input"
  />
);

const CrmV2OpportunityFormView = ({
  lead,
  stages = [],
  users = [],
  teams = [],
  partners = [],
  tags: tagsProp = [],
  sources = [],
  mediums = [],
  campaigns = [],
  lostReasons = [],
  pagination,
  onBack,
  onStageChange,
  onUpdate,
  onWon,
  onLost,
  onNewQuotation,
  onCreate,
  onAction,
  onNext,
  onPrev,
  creationGroupName,
}) => {
  const navigate = useNavigate();
  const isNew = Boolean(lead?._isNew || !lead?.id);
  // Hydrate extras : priorité au nouveau champ properties (backend ROUTES.md 1159+), fallback hack description
  const hydrated = useMemo(() => {
    if (!lead) return { cleanDescription: "", extras: null };
    if (lead.properties && typeof lead.properties === "object" && Object.keys(lead.properties).length) {
      const props = lead.properties;
      // properties peut être objet plat ou {category,...} + properties array
      return { cleanDescription: lead.description ? parseExtrasFromDescription(lead.description).cleanDescription : (lead.description || ""), extras: props.properties ? props : { ...props, properties: props.properties } };
    }
    return parseExtrasFromDescription(lead.description);
  }, [lead?.description, lead?.properties]);
  const initialExtras = hydrated.extras || {};
  const [localLead, setLocalLead] = useState(() => {
    const base = lead || {};
    // remplace description par cleanDescription pour l'édition
    if (hydrated.cleanDescription !== undefined) return { ...base, description: hydrated.cleanDescription };
    return base;
  });
  const [activeTab, setActiveTab] = useState("requests");
  const [actionOpen, setActionOpen] = useState(false);
  const [probability, setProbability] = useState(lead?.probability ?? "");
  const [extraFields, setExtraFields] = useState({
    category: initialExtras.category || lead?.category || "",
    type: initialExtras.type_detail || lead?.type_detail || "",
    vehicle_type: initialExtras.vehicle_type || lead?.vehicle_type || "",
    plate_number: initialExtras.plate_number || lead?.plate_number || "",
  });
  const [extraInfo, setExtraInfo] = useState({
    company_name: initialExtras.company_name || lead?.company_name || lead?.partner_name || lead?.company || "",
    contact_name: initialExtras.contact_name || lead?.contact_name || "",
    contact_title: initialExtras.contact_title || lead?.contact_title || "",
    address: initialExtras.address || lead?.street || lead?.address || "",
    job_position: initialExtras.job_position || lead?.job_position || "",
    city: initialExtras.city || lead?.city || "",
    state: initialExtras.state || lead?.state || "",
    zip: initialExtras.zip || lead?.zip || "",
    country: initialExtras.country || lead?.country || "",
    mobile: lead?.mobile || "",
    website: initialExtras.website || lead?.website || "",
    language: initialExtras.language || lead?.language || "",
    campaign: lead?.campaign_name || "",
    medium: lead?.medium_name || "",
    source: lead?.source_name || lead?.source_id || "",
    referred_by: initialExtras.referred_by || lead?.referred_by || "",
    tracking_company: initialExtras.tracking_company || lead?.company_name || lead?.company || "",
    tracking_team: initialExtras.tracking_team || lead?.team_name || "",
    days_to_assign: lead?.days_to_assign ?? "0.00",
    days_to_close: lead?.days_to_close ?? "0.00",
  });
  const [properties, setProperties] = useState(() => {
    const value = initialExtras.properties || lead?.properties || lead?.custom_fields || [];
    // vehicules / vehicules_display exclus ici → affichage dédié lecture seule (voir vehiclesInfo)
    return normalizeProperties(value);
  });
  const [extrasSaving, setExtrasSaving] = useState(false);
  const saveTimer = React.useRef(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [composer, setComposer] = useState(null);
  const [messageBody, setMessageBody] = useState("");
  const [following, setFollowing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showLostPicker, setShowLostPicker] = useState(false);
  const [lostReasonId, setLostReasonId] = useState("");

  useEffect(() => {
    let ex = {};
    let cleanDescription = lead?.description || "";
    if (lead?.properties && typeof lead.properties === "object" && Object.keys(lead.properties).length) {
      ex = lead.properties;
      cleanDescription = parseExtrasFromDescription(lead?.description).cleanDescription;
    } else {
      const parsed = parseExtrasFromDescription(lead?.description);
      cleanDescription = parsed.cleanDescription;
      ex = parsed.extras || {};
    }
    setLocalLead({ ...(lead || {}), description: cleanDescription });
    setProbability(lead?.probability ?? "");
    setExtraFields({
      category: ex.category || lead?.category || "",
      type: ex.type_detail || lead?.type_detail || "",
      vehicle_type: ex.vehicle_type || lead?.vehicle_type || "",
      plate_number: ex.plate_number || lead?.plate_number || "",
    });
    setExtraInfo({
      company_name: ex.company_name || lead?.company_name || lead?.partner_name || lead?.company || "",
      contact_name: ex.contact_name || lead?.contact_name || "",
      contact_title: ex.contact_title || lead?.contact_title || "",
      address: ex.address || lead?.street || lead?.address || "",
      job_position: ex.job_position || lead?.job_position || "",
      city: ex.city || lead?.city || "",
      state: ex.state || lead?.state || "",
      zip: ex.zip || lead?.zip || "",
      country: ex.country || lead?.country || "",
      mobile: lead?.mobile || "",
      website: ex.website || lead?.website || "",
      language: ex.language || lead?.language || "",
      campaign: lead?.campaign_name || "",
      medium: lead?.medium_name || "",
      source: lead?.source_name || lead?.source_id || "",
      referred_by: ex.referred_by || lead?.referred_by || "",
      tracking_company: ex.tracking_company || lead?.company_name || lead?.company || "",
      tracking_team: ex.tracking_team || lead?.team_name || "",
      days_to_assign: lead?.days_to_assign ?? "0.00",
      days_to_close: lead?.days_to_close ?? "0.00",
    });
    const propertyValue = ex.properties || lead?.properties || lead?.custom_fields || [];
    // vehicules / vehicules_display exclus ici → affichage dédié lecture seule (voir vehiclesInfo)
    setProperties(normalizeProperties(propertyValue));
  }, [lead]);

  const loadChatter = useCallback(async () => {
    if (!lead?.id) return;
    const [messageResponse, historyResponse, attachmentResponse, activityResponse] = await Promise.all([
      api.crm.messages(lead.id).catch(() => []),
      api.crm.history(lead.id).catch(() => []),
      api.crm.attachments(lead.id).catch(() => []),
      api.crm.activities.list({ lead_id: lead.id, limit: 100 }).catch(() => []),
    ]);
    const messageRows = normalize(messageResponse);
    const historyRows = normalize(historyResponse);
    const attachmentRows = normalize(attachmentResponse);
    const activityRows = normalize(activityResponse);
    setMessages(Array.isArray(messageRows) ? messageRows : []);
    setHistory(Array.isArray(historyRows) ? historyRows : []);
    setAttachments(Array.isArray(attachmentRows) ? attachmentRows : []);
    // GET /crm-activities retourne {data:[]} ; fallback sur lead.activities si présent (GET /crm-leads/:id)
    const fromLead = Array.isArray(lead?.activities) ? lead.activities : [];
    const acts = Array.isArray(activityRows) && activityRows.length ? activityRows : fromLead;
    setActivities(acts);
  }, [lead?.id, lead?.activities]);

  useEffect(() => {
    loadChatter();
  }, [loadChatter]);

  // Persistance debouncée — évite le lag à chaque frappe
  const persistExtras = useCallback(async (nextExtraFields, nextExtraInfo, nextProperties, nextCleanDescription) => {
    const cleanDesc = nextCleanDescription !== undefined ? nextCleanDescription : (localLead.description || "");
    const ef = nextExtraFields || extraFields;
    const ei = nextExtraInfo || extraInfo;
    const props = nextProperties !== undefined ? nextProperties : properties;
    const extras = buildExtrasObject(ef, ei, props);
    if (isNew) {
      setLocalLead((c) => ({ ...c, description: cleanDesc }));
      return;
    }
    setLocalLead((c) => ({ ...c, description: cleanDesc }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setExtrasSaving(true);
      try {
        await onUpdate?.({ description: cleanDesc, properties: extras, extra_fields: extras });
      } catch (e) {
        try {
          const fallbackDesc = serializeExtrasToDescription(cleanDesc, extras);
          await onUpdate?.({ description: fallbackDesc });
        } catch {}
        setSaveError(e.message || "Extras save failed");
      } finally {
        setExtrasSaving(false);
      }
    }, 500);
  }, [localLead.description, extraFields, extraInfo, properties, isNew, onUpdate]);

  const updateLead = async (key, value) => {
    if (key === "description") {
      setLocalLead((current) => ({ ...current, [key]: value }));
      if (isNew) return;
      if (onUpdate) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
          try {
            setSaveError(null);
            await onUpdate({ description: value || "", properties: buildExtrasObject(extraFields, extraInfo, properties) });
          } catch (e) {
            setSaveError(e.message || "Update failed");
          }
        }, 500);
      }
      return;
    }
    setLocalLead((current) => ({ ...current, [key]: value }));
    if (LOCAL_ONLY_FIELDS.has(key)) {
      return;
    }
    if (!isNew && onUpdate && CRM_UPDATE_FIELDS.has(key)) {
      const patch = sanitizePatch({ [key]: value });
      if (Object.keys(patch).length === 0) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          setSaveError(null);
          await onUpdate(patch);
        } catch (e) {
          setSaveError(e.message || "Update failed");
        }
      }, 500);
    }
  };

  const saveNewLead = async () => {
    const name = String(localLead.name || "").trim();
    if (!name) {
      setSaveError("Le nom est requis");
      return;
    }
    const extras = buildExtrasObject(extraFields, extraInfo, properties);
    const raw = Object.fromEntries(Object.entries(localLead).filter(([key, value]) => key !== "_isNew" && CRM_UPDATE_FIELDS.has(key) && value !== "" && value !== null && value !== undefined));
    const payload = sanitizePatch({ ...raw, name, stage_id: raw.stage_id || localLead.stage_id || stages.find((stage) => !stage.is_won && !stage.is_lost)?.id, description: localLead.description || "" });
    if (Object.keys(extras).length) { payload.properties = extras; payload.extra_fields = extras; }
    setSaving(true);
    setSaveError(null);
    try {
      await onCreate?.(payload);
    } catch (e) {
      setSaveError(e.message || "Creation failed");
    }
    setSaving(false);
  };

  const updateExtraInfo = async (key, value) => {
    const nextInfo = { ...extraInfo, [key]: value };
    setExtraInfo(nextInfo);
    const fieldMap = {
      company_name: "company",
      mobile: "mobile",
      source: "source_id",
      medium: "medium_id",
      campaign: "campaign_id",
    };
    const mapped = fieldMap[key];
    if (mapped && CRM_UPDATE_FIELDS.has(mapped)) {
      await updateLead(mapped, value);
      // même si mappé, on persiste aussi l'alias local pour cohérence à la relecture
      if (LOCAL_ONLY_FIELDS.has(key) || ["company_name", "tracking_company", "tracking_team"].includes(key)) {
        await persistExtras(extraFields, nextInfo, properties);
      }
      return;
    } else if (CRM_UPDATE_FIELDS.has(key)) {
      await updateLead(key, value);
      return;
    }
    // local-only → persisté via description
    await persistExtras(extraFields, nextInfo, properties);
  };

  const handleExtraFieldChange = async (key, value) => {
    const next = { ...extraFields, [key]: value };
    setExtraFields(next);
    await persistExtras(next, extraInfo, properties);
  };

  const addProperty = async () => {
    const next = [...properties, { name: "New Property", type: "Text", value: "" }];
    setProperties(next);
    await persistExtras(extraFields, extraInfo, next);
  };

  const updateProperty = async (index, key, value) => {
    const next = properties.map((property, propertyIndex) => propertyIndex === index ? { ...property, [key]: value } : property);
    setProperties(next);
    // debounce léger : on ne spam pas l'API à chaque frappe, on attend blur ou 800ms
    // ici on persiste immédiatement pour cohérence, le caller peut aussi utiliser onBlur variant
  };

  const persistPropertyBlur = async () => {
    await persistExtras(extraFields, extraInfo, properties);
  };

  const removeProperty = async (index) => {
    const next = properties.filter((_, propertyIndex) => propertyIndex !== index);
    setProperties(next);
    await persistExtras(extraFields, extraInfo, next);
  };

  const [activityDraft, setActivityDraft] = useState({ summary: "", date_deadline: "", note: "" });

  const sortedPartners = useMemo(() => {
    return [...partners].sort((a, b) => {
      const an = (a.display_name || a.name || "").toLowerCase();
      const bn = (b.display_name || b.name || "").toLowerCase();
      return an.localeCompare(bn);
    });
  }, [partners]);

  const user = users.find((item) => String(item.id) === String(localLead.user_id));
  const salesperson = user?.name || user?.username || user?.email || localLead.user_name || "-";
  const tags = Array.isArray(localLead.tags) && localLead.tags.length ? localLead.tags : localLead.tag_ids || [];
  const [quotationCount, setQuotationCount] = useState(null);
  useEffect(() => {
    if (!lead?.id) { setQuotationCount(0); return; }
    // Priorité aux données déjà incluses dans GET /crm-leads/:id (orders / sale_order_ids) — évite scan 500
    const direct = Array.isArray(lead.orders) ? lead.orders.length : Array.isArray(lead.sale_order_ids) ? lead.sale_order_ids.length : null;
    if (direct !== null) { setQuotationCount(direct); return; }
    let active = true;
    (async () => {
      try {
        const orders = await api.sales.list({ limit: 500 }).catch(() => []);
        if (!active) return;
        const rows = Array.isArray(orders) ? orders : [];
        const cnt = rows.filter((o) => String(o.partner_id) === String(lead.partner_id) || String(o.lead_id) === String(lead.id) || String(o.opportunity_id) === String(lead.id)).length;
        setQuotationCount(cnt);
      } catch { if (active) setQuotationCount(0); }
    })();
    return () => { active = false; };
  }, [lead?.id, lead?.partner_id, lead?.orders, lead?.sale_order_ids]);
  const quotations = quotationCount !== null ? quotationCount : (localLead.quotation_count ?? localLead.quotations_count ?? lead?.sale_order_ids?.length ?? 0);
  const meetings = activities.filter((a) => !a.done && a.state !== "done").length || localLead.meeting_count || 0;

  // Véhicules : lecture seule. Priorité à vehicules_display, fallback tableau
  // construit depuis properties.vehicules. Jamais édité via les champs Properties.
  const vehiclesInfo = useMemo(
    () => extractVehiclesInfo(lead?.properties, initialExtras?.properties ?? initialExtras),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lead?.properties, hydrated.extras]
  );

  const feed = useMemo(() => {
    const rows = [
      ...history.map((item) => ({
        date: item.date || item.created_at || item.create_date,
        author: item.user_name || item.create_uid_name || "System",
        body: item.message || item.description || `${item.field_name || "Updated"}: ${item.old_value || ""} -> ${item.new_value || ""}`,
        type: "history",
      })),
      ...messages.map((item) => ({
        date: item.date || item.created_at || item.create_date,
        author: item.author_name || item.user_name || "System",
        body: item.body || item.message || "",
        type: item.type === "note" ? "note" : "message",
      })),
      ...activities.map((item) => ({
        date: item.date_deadline || item.date || item.create_date,
        author: item.user_name || item.user_login || "System",
        body: `[${item.type_name || item.activity_type_name || "Activity"}] ${item.summary || item.note || ""}${item.date_deadline ? ` — due ${String(item.date_deadline).slice(0,10)}` : ""}${item.done || item.state === "done" ? " ✓ done" : ""}`,
        type: item.done || item.state === "done" ? "activity_done" : "activity",
        raw: item,
      })),
    ];
    if (!rows.length) {
      rows.push({ date: new Date().toISOString(), author: "System", body: "Lead / Opportunity created", type: "history" });
    }
    return rows.sort((left, right) => new Date(right.date) - new Date(left.date));
  }, [history, messages, activities]);

  const submitMessage = async () => {
    if (!messageBody.trim() || !lead?.id) return;
    await api.crm.addMessage(lead.id, { body: messageBody.trim(), type: composer === "note" ? "note" : "message" }).catch(() => {});
    setMessageBody("");
    setComposer(null);
    loadChatter();
  };

  const toggleFollowing = async () => {
    if (!lead?.id) { setFollowing((v) => !v); return; }
    const next = !following;
    setFollowing(next);
    try {
      if (next) await api.crm.follow(lead.id, {}).catch(() => api.crm.addMessage(lead.id, { body: "Followed", type: "note" }));
      else await api.crm.unfollow(lead.id).catch(() => {});
    } catch {}
  };

  const createMeetingActivity = async () => {
    if (!lead?.id) { setComposer("activity"); return; }
    if (!activityDraft.summary.trim()) return;
    try {
      await api.crm.activities.create({ lead_id: lead.id, res_id: lead.id, res_model: "crm.lead", summary: activityDraft.summary, date_deadline: activityDraft.date_deadline || new Date(Date.now() + 86400000).toISOString().slice(0,10), note: activityDraft.note, activity_type_id: 1 });
      setActivityDraft({ summary: "", date_deadline: "", note: "" });
      setComposer(null);
      loadChatter();
    } catch (e) { setSaveError(e.message || "Activity failed"); }
  };

  const handleActivityDone = async (activity) => {
    try {
      await api.crm.activities.done(activity.id);
      loadChatter();
    } catch (e) { setSaveError(e.message || "Done failed"); }
  };

  const handleActivitySnooze = async (activity) => {
    const input = window.prompt(`Reporter de combien de jours ? (défaut 3)`, "3");
    if (input === null) return;
    try {
      await api.crm.activities.snooze(activity.id, { days: parseInt(input, 10) || 3 });
      loadChatter();
    } catch (e) { setSaveError(e.message || "Snooze failed"); }
  };

  return (
    <div className="crm-detail-page">
      <header className="crm-detail-header">
        <div className="crm-header-top">
          <div className="crm-breadcrumb">
            <button type="button" onClick={onBack}>Pipeline</button>
            <span>/</span>
            <strong>{localLead.name || "Opportunity"}</strong>
          </div>
          <div className="crm-header-tools">
            {isNew && <button type="button" className="crm-save-button" disabled={!String(localLead.name || "").trim() || saving} onClick={saveNewLead}>{saving ? "Saving…" : "Save"}</button>}
            <div style={{ position: "relative" }}>
              <button type="button" className="crm-outline-button" onClick={() => setActionOpen((value) => !value)}>
                <Settings2 size={12} /> Action
              </button>
              {actionOpen && (
                <>
                  <div className="crm-menu-backdrop" onClick={() => setActionOpen(false)} />
                  <div className="crm-action-menu">
                    {[
                      ["Archive", Archive, "archive"],
                      ["Duplicate", Copy, "duplicate"],
                      ["Send email", Mail, "email"],
                      ["Send WhatsApp message", MessageCircle, "whatsapp"],
                      ["Send ChatRoom message", MessagesSquare, "chatroom"],
                      ["Enrich", WandSparkles, "enrich"],
                    ].map(([label, Icon, action]) => (
                      <button key={action} type="button" onClick={() => { setActionOpen(false); onAction && onAction(action); }}>
                        <Icon size={16} /> {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {pagination ? (
              <div className="crm-pagination">
                <IconButton label="Previous" onClick={onPrev}><ChevronLeft size={14} /></IconButton>
                <span>{pagination.current} / {pagination.total}</span>
                <IconButton label="Next" onClick={onNext}><ChevronRight size={14} /></IconButton>
              </div>
            ) : null}
            <button type="button" className="crm-new-button" onClick={() => onAction && onAction("new")}>New</button>
          </div>
        </div>
        <div className="crm-header-bottom">
          <div className="crm-lifecycle-actions">
            <button type="button" onClick={onNewQuotation} disabled={isNew}>New Quotation</button>
            <button type="button" onClick={onWon} disabled={isNew}>Won</button>
            <button type="button" onClick={() => setShowLostPicker(true)} disabled={isNew}>Lost</button>
          </div>
          <StageBar stages={stages.filter((stage) => stage.is_active !== false)} currentStageId={localLead.stage_id} onSelect={(stage) => {
            if (isNew) {
              setLocalLead((c) => ({ ...c, stage_id: stage.id }));
              return;
            }
            if (stage.is_won) { onWon && onWon(); return; }
            if (stage.is_lost) { setShowLostPicker(true); return; }
            onStageChange && onStageChange(stage.id);
          }} />
        </div>
        {showLostPicker && (
          <div style={{ padding: "8px 18px", background: "#fff1f2", borderTop: "1px solid #fecdd3", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#be123c", fontWeight: 600 }}>Motif de perte :</span>
            <select value={lostReasonId} onChange={(e) => setLostReasonId(e.target.value)} className="crm-input" style={{ minWidth: 180, maxWidth: 260 }}>
              <option value="">Select reason…</option>
              {lostReasons.map((r, i) => <option key={r.id ?? `lost-${i}`} value={r.id}>{r.name}</option>)}
            </select>
            <button type="button" className="crm-save-button" style={{ background: "#be123c" }} disabled={!lostReasonId} onClick={async () => { const id = lostReasonId; setShowLostPicker(false); setLostReasonId(""); await onLost?.(id); }}>Confirm Lost</button>
            <button type="button" className="crm-outline-button" onClick={() => setShowLostPicker(false)}>Cancel</button>
          </div>
        )}
      </header>

      {isNew && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", background: creationGroupName ? "#eef2f7" : "#fff7ed", borderBottom: "1px solid #dee2e6", fontSize: 12.5, color: ODOO.text }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: creationGroupName ? ODOO.primary : "#f59e0b", flexShrink: 0 }} />
          {creationGroupName
            ? <>This prospect will be created in group <strong>{creationGroupName}</strong>.</>
            : <>No group selected: this prospect will be created <strong>without a group</strong> and can be assigned later.</>}
        </div>
      )}

      <main className="crm-detail-content">
        <section className="crm-opportunity-sheet">
          <div className="crm-smart-buttons">
            <SmartButton icon={MessageCircle} label="Send" onClick={() => onAction && onAction("chatroom")} />
            <SmartButton icon={FileText} label="Requests" count={attachments.length} onClick={() => setActiveTab("requests")} />
            <SmartButton icon={Calendar} label="Meeting" count={meetings} onClick={() => setComposer("activity")} />
            <SmartButton icon={FileBarChart} label="Quotations" count={quotations} onClick={() => onAction && onAction("quotations")} />
          </div>

          <div className="crm-opportunity-heading">
            {isNew ? <input className="crm-new-title" autoFocus value={localLead.name || ""} onChange={(event) => setLocalLead((c) => ({ ...c, name: event.target.value }))} placeholder="e.g. Product Pricing *" /> : <input className="crm-new-title" value={localLead.name || ""} onChange={(event) => setLocalLead((c) => ({ ...c, name: event.target.value }))} onBlur={(e) => updateLead("name", e.target.value)} />}
            {isNew && !String(localLead.name || "").trim() && <div style={{ color: "#be123c", fontSize: 12, marginTop: 6 }}>Le nom est requis pour créer l'opportunité</div>}
            <div className="crm-kpis">
              <div><small>Expected Revenue</small><strong><em>FCFA</em> <input type="number" value={localLead.expected_revenue ?? ""} onChange={(e) => setLocalLead((c) => ({ ...c, expected_revenue: e.target.value }))} onBlur={(e) => updateLead("expected_revenue", e.target.value)} placeholder="0" style={{ width: 120, border: 0, borderBottom: "1px solid #dee2e6", textAlign: "right", font: "18px inherit" }} /></strong></div>
              <div><small>Probability</small><strong><em>at</em><input value={probability} onChange={(event) => setProbability(event.target.value)} onBlur={() => updateLead("probability", Number(probability) || 0)} /> %</strong></div>
            </div>
          </div>

          <div className="crm-form-grid">
            <div>
              <Field label="Customer">
                <div className="crm-inline-input">
                  <select
                    value={localLead.partner_id || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const partner = partners.find((p) => String(p.id) === String(v));
                      setLocalLead((c) => ({ ...c, partner_id: v ? parseInt(v, 10) : null, partner_name: partner?.display_name || partner?.name || "", company: partner?.display_name || partner?.name || c.company }));
                      if (!isNew && v) {
                        const pid = parseInt(v, 10);
                        sanitizePatch({ partner_id: pid });
                        onUpdate?.({ partner_id: pid }).catch(() => {});
                      } else if (!isNew && !v) {
                        onUpdate?.({ partner_id: null }).catch(() => {});
                      }
                      if (partner?.email && !localLead.email_from) updateLead("email_from", partner.email);
                      if (partner?.phone && !localLead.phone) updateLead("phone", partner.phone);
                    }}
                    className="crm-input"
                    style={{ flex: 1 }}
                  >
                    <option value="">{localLead.company || "Select customer…"}</option>
                    {sortedPartners.map((p, idx) => (
                      <option key={p.id ?? `partner-${idx}`} value={p.id}>{p.display_name || p.name} {p.email ? `· ${p.email}` : ""}</option>
                    ))}
                  </select>
                  {/* fallback texte libre pour company si pas de partner */}
                  {!localLead.partner_id && (
                    <input
                      value={localLead.company || ""}
                      onChange={(e) => updateLead("company", e.target.value)}
                      placeholder="or company name"
                      className="crm-input"
                      style={{ flex: 1, marginLeft: 6 }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => navigate("/admin-panel/crm/customers")}
                    title="Voir tous les clients"
                    aria-label="Voir tous les clients"
                    style={{ border: "none", background: "transparent", cursor: "pointer", padding: 4, display: "inline-flex", alignItems: "center", color: ODOO.primary }}
                  >
                    <ExternalLink size={14} />
                  </button>
                </div>
                <div style={{ fontSize: 11, color: ODOO.muted, marginTop: 4 }}>{sortedPartners.length} client{sortedPartners.length !== 1 ? "s" : ""} disponible{sortedPartners.length !== 1 ? "s" : ""}</div>
              </Field>
              <Field label="Email"><div className="crm-inline-input"><Mail size={12} color={ODOO.muted} /><Input value={localLead.email_from} placeholder="email@example.com" onChange={(value) => updateLead("email_from", value)} /><a href={localLead.email_from ? `mailto:${localLead.email_from}` : undefined} style={{ color: ODOO.primary, display: localLead.email_from ? "inline-flex" : "none", alignItems: "center" }} title="Send email"><Mail size={14} /></a></div></Field>
              <Field label="Phone"><div className="crm-inline-input"><Phone size={12} color={ODOO.muted} /><Input value={localLead.phone} placeholder="+237 ..." onChange={(value) => updateLead("phone", value)} /><a href={localLead.phone ? `tel:${localLead.phone}` : undefined} style={{ color: ODOO.primary, display: localLead.phone ? "inline-flex" : "none", alignItems: "center" }} title="Call"><Phone size={14} /></a></div></Field>
            </div>
            <div>
              <Field label="Salesperson">
                <select
                  value={localLead.user_id || ""}
                  onChange={(e) => updateLead("user_id", e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="crm-input"
                >
                  <option value="">Unassigned…</option>
                  {users.map((u, idx) => (
                    <option key={u.id ?? `user-${idx}`} value={u.id}>{u.name || u.username || u.email || u.login}</option>
                  ))}
                </select>
              </Field>
              <Field label="Sales Team">
                <select
                  value={localLead.team_id || ""}
                  onChange={(e) => updateLead("team_id", e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="crm-input"
                >
                  <option value="">No team…</option>
                  {teams.map((t, idx) => (
                    <option key={t.id ?? `team-${idx}`} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Expected Closing"><div className="crm-inline-input"><Calendar size={12} color={ODOO.muted} /><Input type="date" value={localLead.date_deadline ? String(localLead.date_deadline).slice(0, 10) : ""} onChange={(value) => updateLead("date_deadline", value)} /></div></Field>
              <Field label="Priority"><div className="crm-stars">{[0, 1, 2].map((index) => <button type="button" key={index} onClick={() => updateLead("priority", index < Number(localLead.priority || 0) ? index : index + 1)}><Star size={16} fill={index < Number(localLead.priority || 0) ? ODOO.star : "none"} color={index < Number(localLead.priority || 0) ? ODOO.star : "#adb5bd"} /></button>)}</div></Field>
              <Field label="Tags">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  {tagsProp.length === 0 ? <small style={{ color: "#94a3b8" }}>-</small> : tagsProp.slice(0, 30).map((tg) => {
                    const active = Array.isArray(localLead.tag_ids) && localLead.tag_ids.map(String).includes(String(tg.id));
                    return (
                      <button
                        key={tg.id}
                        type="button"
                        onClick={() => {
                          const cur = Array.isArray(localLead.tag_ids) ? localLead.tag_ids.map(String) : [];
                          const next = active ? cur.filter((x) => x !== String(tg.id)) : [...cur, String(tg.id)];
                          const ids = next.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n));
                          setLocalLead((c) => ({ ...c, tag_ids: ids, tags: ids.map((id) => tagsProp.find((t) => String(t.id) === String(id))?.name || String(id)) }));
                          if (!isNew) onUpdate?.({ tag_ids: ids }).catch(() => {});
                        }}
                        style={{
                          padding: "3px 9px",
                          borderRadius: 10,
                          border: active ? `1px solid ${ODOO.primary}` : "1px solid #cce3e3",
                          background: active ? "#f3eef2" : "#eef6f3",
                          color: active ? ODOO.primary : "#017e84",
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        {tg.name}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </div>
          <div style={{ padding: "16px 24px 0", maxWidth: "100%", boxSizing: "border-box", overflow: "hidden" }}>
            {(vehiclesInfo.display || vehiclesInfo.list.length > 0) && (
              <div style={{ marginBottom: 16, border: "1px solid #e9ecef", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", background: "#f8f9fa", borderBottom: "1px solid #e9ecef", fontSize: 13, fontWeight: 700, color: "#495057" }}>
                  Véhicules
                  <span style={{ fontWeight: 400, color: "#0e7a7d", fontSize: 11, marginLeft: 8 }}>lecture seule (backend)</span>
                </div>
                {vehiclesInfo.display ? (
                  <div style={{ padding: "10px 12px", fontSize: 13, color: "#212529", whiteSpace: "pre-wrap" }}>{vehiclesInfo.display}</div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#fcfcfc", color: "#6c757d", textAlign: "left" }}>
                        <th style={{ padding: "8px 12px", fontWeight: 600 }}>Modèle</th>
                        <th style={{ padding: "8px 12px", fontWeight: 600 }}>Immatriculation</th>
                        <th style={{ padding: "8px 12px", fontWeight: 600, textAlign: "right" }}>Montant</th>
                        <th style={{ padding: "8px 12px", fontWeight: 600 }}>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehiclesInfo.list.map((v, i) => (
                        <tr key={i} style={{ borderTop: "1px solid #f1f3f5" }}>
                          <td style={{ padding: "8px 12px" }}>{v.modele || "-"}</td>
                          <td style={{ padding: "8px 12px" }}>{v.immatriculation || "-"}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right" }}>{v.montant === "" || v.montant === null ? "-" : v.montant}</td>
                          <td style={{ padding: "8px 12px" }}>{v.statut || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            <button type="button" className="crm-property-button" onClick={addProperty} style={{ margin: 0 }}><Plus size={14} /> Add a Property {extrasSaving ? "(saving…)" : null}</button>
            {properties.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "0 24px", marginTop: 16, maxWidth: "100%" }}>
                {properties.map((property, index) => (
                  <div className="crm-field" key={`${property.name}-${index}`} style={{ minHeight: 58, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", minWidth: 0, maxWidth: "100%" }}>
                        <input value={property.name || ""} onChange={(event) => { const n=[...properties]; n[index]={...n[index], name:event.target.value}; setProperties(n); }} onBlur={persistPropertyBlur} placeholder="Nom" aria-label="Property name" style={{ flex: 1, minWidth: 0, maxWidth: "100%", border: 0, borderBottom: "1px solid #e9ecef", textAlign: "right", font: "13px 'Lucida Grande', Helvetica, Verdana, Arial, sans-serif", padding: "10px 0", outline: 0, background: "transparent", color: "#495057", boxSizing: "border-box" }} />
                        <select value={property.type || "Text"} onChange={(event) => { const n=[...properties]; n[index]={...n[index], type:event.target.value}; setProperties(n); }} onBlur={persistPropertyBlur} aria-label="Property type" style={{ flexShrink: 0, border: 0, borderBottom: "1px solid #e9ecef", fontSize: 11, color: "#6c757d", background: "transparent", padding: "6px 2px", maxWidth: 80 }}>
                          <option>Text</option>
                          <option>Number</option>
                          <option>Checkbox</option>
                          <option>Date</option>
                        </select>
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, maxWidth: "100%" }}>
                        <input value={property.value || ""} onChange={(event) => { const n=[...properties]; n[index]={...n[index], value:event.target.value}; setProperties(n); }} onBlur={persistPropertyBlur} placeholder="Valeur" aria-label="Property default value" className="crm-input" style={{ flex: 1, minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }} />
                        <button type="button" onClick={() => removeProperty(index)} aria-label="Delete property" style={{ flexShrink: 0, border: 0, background: "transparent", color: "#dc3545", cursor: "pointer", display: "inline-flex", padding: 4 }}><Trash2 size={14} /></button>
                      </div>
                    </div>
                ))}
              </div>
            )}
          </div>

          <div className="crm-tabs">
            {[['requests', 'Request Information'], ['notes', 'Internal Notes']].map(([id, label]) => <button type="button" key={id} className={activeTab === id ? "active" : ""} onClick={() => setActiveTab(id)}>{label}</button>)}
          </div>
          <div className="crm-tab-content">
            {activeTab === "requests" && <div className="crm-request-fields"><h2>Request Information <small style={{ fontWeight: 400, textTransform: "none", color: "#0e7a7d", background: "#e6f6f6", padding: "2px 6px", borderRadius: 3 }}>{extrasSaving ? "saving…" : "persisté via description"}</small></h2>{[['Category', 'category'], ['Type', 'type'], ['Vehicle Type', 'vehicle_type'], ['Plate Number', 'plate_number']].map(([label, key]) => <label key={key}><span>{label}</span><input value={extraFields[key]} onChange={(event) => handleExtraFieldChange(key, event.target.value)} onBlur={() => persistExtras(extraFields, extraInfo, properties)} placeholder="Stocké dans description (serveur)" /></label>)}</div>}
            {activeTab === "notes" && <><textarea className="crm-notes" value={localLead.description || ""} placeholder="Internal notes (saved to description, extras inclus)" onChange={(event) => setLocalLead((c) => ({ ...c, description: event.target.value }))} onBlur={(e) => updateLead("description", e.target.value)} /><div style={{ fontSize: 11, color: "#6c757d", marginTop: 6 }}>Les champs Extra / Request / Properties sont encodés dans cette description (marqueur <code>{'<!--CRM_EXTRAS:...-->'}</code>). Ne pas supprimer le marqueur.</div></>}
            {activeTab === "extra" && (
              <div className="crm-extra-information">
                <div className="crm-extra-column">
                  <h2>Contact Information <small style={{ fontWeight: 400, color: "#0e7a7d", fontSize: 10, marginLeft: 6 }}>persisté via description</small></h2>
                  <Field label="Company Name"><Input value={extraInfo.company_name} onChange={(value) => updateExtraInfo("company_name", value)} /></Field>
                  <Field label="Address"><Input value={extraInfo.address} onChange={(value) => updateExtraInfo("address", value)} placeholder="Persisté via description" /></Field>
                  <div className="crm-address-grid"><Input value={extraInfo.city} placeholder="City" onChange={(value) => updateExtraInfo("city", value)} /><Input value={extraInfo.state} placeholder="State" onChange={(value) => updateExtraInfo("state", value)} /><Input value={extraInfo.zip} placeholder="ZIP" onChange={(value) => updateExtraInfo("zip", value)} /></div>
                  <Input value={extraInfo.country} placeholder="Country" onChange={(value) => updateExtraInfo("country", value)} />
                  <Field label="Website"><Input value={extraInfo.website} placeholder="https://www.example.com" onChange={(value) => updateExtraInfo("website", value)} /></Field>
                  <Field label="Language"><Input value={extraInfo.language} placeholder="Language" onChange={(value) => updateExtraInfo("language", value)} /></Field>
                </div>
                <div className="crm-extra-column">
                  <h2>&nbsp;</h2>
                  <Field label="Contact Name"><Input value={extraInfo.contact_name} placeholder="" onChange={(value) => updateExtraInfo("contact_name", value)} /></Field>
                  <Field label="Title"><Input value={extraInfo.contact_title} placeholder="" onChange={(value) => updateExtraInfo("contact_title", value)} /></Field>
                  <Field label="Job Position"><Input value={extraInfo.job_position} placeholder="" onChange={(value) => updateExtraInfo("job_position", value)} /></Field>
                  <Field label="Mobile"><Input value={extraInfo.mobile} onChange={(value) => updateExtraInfo("mobile", value)} /></Field>
                </div>
                <div className="crm-extra-column">
                  <h2>Marketing</h2>
                  <Field label="Campaign">
                    <select value={localLead.campaign_id || ""} onChange={(e) => updateLead("campaign_id", e.target.value ? parseInt(e.target.value, 10) : null)} className="crm-input">
                      <option value="">None…</option>
                      {campaigns.map((c, i) => <option key={c.id ?? `campaign-${i}`} value={c.id}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Medium">
                    <select value={localLead.medium_id || ""} onChange={(e) => updateLead("medium_id", e.target.value ? parseInt(e.target.value, 10) : null)} className="crm-input">
                      <option value="">None…</option>
                      {mediums.map((m, i) => <option key={m.id ?? `medium-${i}`} value={m.id}>{m.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Source">
                    <select value={localLead.source_id || ""} onChange={(e) => updateLead("source_id", e.target.value ? parseInt(e.target.value, 10) : null)} className="crm-input">
                      <option value="">None…</option>
                      {sources.map((s, i) => <option key={s.id ?? `source-${i}`} value={s.id}>{s.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Referred By"><Input value={extraInfo.referred_by} placeholder="" onChange={(value) => updateExtraInfo("referred_by", value)} /></Field>
                </div>
                <div className="crm-extra-column">
                  <h2>Tracking</h2>
                  <Field label="Company"><Input value={extraInfo.tracking_company} placeholder="Derived from Customer" onChange={(value) => updateExtraInfo("tracking_company", value)} /></Field>
                  <Field label="Sales Team"><Input value={extraInfo.tracking_team || teams.find((team) => String(team.id) === String(localLead.team_id))?.name || ""} placeholder="Derived from Sales Team" onChange={(value) => updateExtraInfo("tracking_team", value)} /></Field>
                  <Field label="Days to Assign"><Input value={extraInfo.days_to_assign} type="text" placeholder="Computed (read-only)" disabled /></Field>
                  <Field label="Days to Close"><Input value={extraInfo.days_to_close} type="text" placeholder="Computed (read-only)" disabled /></Field>
                </div>
              </div>
            )}
          </div>
          {saveError && <div style={{ margin: "12px 36px", padding: "8px 12px", background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", fontSize: 13, borderRadius: 4 }}>{saveError}</div>}
        </section>

        <section className="crm-chatter">
          <div className="crm-chatter-toolbar">
            <div><button type="button" className={composer === "message" ? "active" : ""} onClick={() => setComposer("message")}><Send size={11} /> Send message</button><button type="button" className={composer === "note" ? "active" : ""} onClick={() => setComposer("note")}><StickyNote size={11} /> Log note</button><button type="button" className={composer === "activity" ? "active" : ""} onClick={() => setComposer(composer === "activity" ? null : "activity")}><Clock3 size={11} /> Activities</button></div>
            <div className="crm-chatter-meta"><span title="Attachments"><Paperclip size={12} /> {attachments.length}</span><span title="Followers"><Users size={12} /> {following ? "●" : "○"}</span><button type="button" onClick={toggleFollowing}><Eye size={12} /> {following ? "Following" : "Follow"}</button></div>
          </div>
          {composer === "activity" && <div className="crm-composer"><div style={{ display: "grid", gap: 8 }}><input value={activityDraft.summary} onChange={(e) => setActivityDraft((c) => ({ ...c, summary: e.target.value }))} placeholder="Meeting summary — e.g. Call client" style={{ width: "100%", padding: "8px 10px", border: "1px solid #dee2e6", fontFamily: pageFont, fontSize: 13 }} /><div style={{ display: "flex", gap: 8 }}><input type="date" value={activityDraft.date_deadline} onChange={(e) => setActivityDraft((c) => ({ ...c, date_deadline: e.target.value }))} style={{ flex: 1, padding: "8px 10px", border: "1px solid #dee2e6", fontFamily: pageFont, fontSize: 13 }} /><input value={activityDraft.note} onChange={(e) => setActivityDraft((c) => ({ ...c, note: e.target.value }))} placeholder="Note (optional)" style={{ flex: 2, padding: "8px 10px", border: "1px solid #dee2e6", fontFamily: pageFont, fontSize: 13 }} /></div></div><div><button type="button" onClick={() => { setComposer(null); setActivityDraft({ summary: "", date_deadline: "", note: "" }); }}>Discard</button><button type="button" className="primary" disabled={!activityDraft.summary.trim()} onClick={createMeetingActivity}>Schedule</button></div></div>}
          {composer && composer !== "activity" && <div className="crm-composer"><textarea value={messageBody} onChange={(event) => setMessageBody(event.target.value)} placeholder={composer === "note" ? "Log an internal note" : "Send a message to the customer"} /><div><button type="button" onClick={() => { setComposer(null); setMessageBody(""); }}>Discard</button><button type="button" className="primary" disabled={!messageBody.trim()} onClick={submitMessage}>{composer === "note" ? "Log" : "Send"}</button></div></div>}
          <div className="crm-feed">{feed.map((item, index) => <article key={`${item.date}-${index}`}><span className="crm-feed-avatar">{getInitials(item.author)}</span><div><header><strong>{item.author}</strong><small>{relativeTime(item.date)}</small>{item.type === "activity" && <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 4, background: "#fff3cd", color: "#856404", fontSize: 10, fontWeight: 700 }}>ACTIVITY</span>}{item.type === "activity_done" && <span style={{ marginLeft: 8, padding: "1px 6px", borderRadius: 4, background: "#d4edda", color: "#155724", fontSize: 10, fontWeight: 700 }}>DONE</span>}</header><p>{item.body}</p>{item.type === "activity" && item.raw && <div style={{ display: "flex", gap: 6, marginTop: 6 }}><button type="button" onClick={() => handleActivityDone(item.raw)} style={{ padding: "3px 8px", border: "1px solid #28a745", background: "#fff", color: "#28a745", borderRadius: 3, fontSize: 11, cursor: "pointer" }}>Done</button><button type="button" onClick={() => handleActivitySnooze(item.raw)} style={{ padding: "3px 8px", border: "1px solid #ffc107", background: "#fff", color: "#856404", borderRadius: 3, fontSize: 11, cursor: "pointer" }}>Snooze</button></div>}</div></article>)}</div>
        </section>
      </main>

      <style>{`
        .crm-detail-page { min-height: calc(100vh - 80px); background: #f8f9fa; color: #212529; font-family: ${pageFont}; font-size: 14px; }
        .crm-detail-header { background: #fff; border-bottom: 1px solid #dee2e6; }
        .crm-header-top, .crm-header-bottom { display: flex; align-items: center; box-sizing: border-box; }
        .crm-header-top { min-height: 48px; justify-content: space-between; gap: 16px; padding: 9px 18px; }
        .crm-header-bottom { min-height: 38px; gap: 16px; padding: 0 18px 8px; }
        .crm-breadcrumb { display: flex; align-items: center; gap: 6px; min-width: 0; color: #6c757d; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .crm-breadcrumb button { padding: 0; border: 0; background: transparent; color: #714b67; font: inherit; cursor: pointer; }
        .crm-breadcrumb strong { overflow: hidden; text-overflow: ellipsis; color: #212529; font-weight: 600; }
        .crm-header-tools, .crm-pagination, .crm-lifecycle-actions, .crm-chatter-toolbar, .crm-chatter-meta { display: flex; align-items: center; }
        .crm-header-tools { gap: 8px; flex-shrink: 0; }
        .crm-outline-button, .crm-new-button, .crm-lifecycle-actions button, .crm-chatter-toolbar button, .crm-composer button { font: inherit; cursor: pointer; }
        .crm-outline-button { display: inline-flex; align-items: center; gap: 5px; padding: 8px 11px; font-size: 13px; }
        .crm-new-button { border: 0; border-radius: 2px; padding: 9px 15px; background: #714b67; color: #fff; font-weight: 700; font-size: 13px; }
        .crm-save-button { border: 0; border-radius: 2px; padding: 9px 15px; background: #008784; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; }
        .crm-save-button:disabled { opacity: .5; cursor: default; }
        .crm-pagination { gap: 6px; color: #6c757d; font-size: 13px; }
        .crm-pagination span { min-width: 68px; text-align: center; }
        .crm-lifecycle-actions { gap: 2px; flex-shrink: 0; }
        .crm-lifecycle-actions button { border: 0; background: transparent; color: #714b67; padding: 8px 11px; font-size: 13px; font-weight: 700; text-transform: uppercase; }
        .crm-lifecycle-actions button:first-child { background: #008784; color: #fff; border-radius: 1px; }
        .crm-stage-bar { display: flex; align-items: stretch; height: 38px; flex: 1; min-width: 0; overflow: hidden; border: 1px solid #dee2e6; background: #e9ecef; }
        .crm-detail-content { width: min(72%, 980px); margin: 0 auto; padding: 20px 0 40px; }
        .crm-opportunity-sheet, .crm-chatter { background: #fff; border: 1px solid #dee2e6; border-radius: 3px; box-shadow: 0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04); }
        .crm-opportunity-sheet { overflow: hidden; }
        .crm-smart-buttons { display: flex; justify-content: flex-end; height: 56px; border-bottom: 1px solid #e9ecef; }
        .crm-smart-button { display: inline-flex; align-items: center; gap: 7px; min-width: 112px; padding: 0 16px; border: 0; border-left: 1px solid #dee2e6; background: #fff; color: #495057; font: 14px ${pageFont}; cursor: pointer; }
        .crm-smart-button strong { color: #714b67; font-size: 13px; }
        .crm-opportunity-heading { padding: 28px 30px 20px; border-bottom: 1px solid #e9ecef; background: #fff; }
        .crm-opportunity-heading h1 { margin: 0; font-size: 27px; line-height: 1.3; font-weight: 500; }
        .crm-opportunity-heading .crm-new-title { text-align: center; display: block; margin: 0 auto; font-size: 28px; font-weight: 600; letter-spacing: -0.01em; }
        .crm-new-title { width: 82%; border: 0; border-bottom: 1px solid #6c757d; outline: 0; padding: 0 0 7px; background: transparent; color: #212529; font: 27px ${pageFont}; }
        .crm-kpis { display: flex; gap: 52px; margin-top: 16px; }
        .crm-kpis div { display: flex; flex-direction: column; gap: 3px; }
        .crm-kpis small { color: #6c757d; font-size: 13px; letter-spacing: .02em; }
        .crm-kpis strong { display: flex; align-items: center; gap: 6px; font-size: 19px; font-weight: 700; color: #017e84; border-bottom: 1px solid #e9ecef; padding-bottom: 6px; }
        .crm-kpis em { color: #6c757d; font-size: 13px; font-style: normal; font-weight: 400; }
        .crm-kpis input { width: 58px; border: 0; outline: 0; text-align: right; font: inherit; }
        .crm-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 36px 42px; padding: 32px 36px 36px; background: #fff; }
        .crm-field { display: grid; grid-template-columns: 140px minmax(0, 1fr); align-items: center; gap: 14px; min-height: 58px; }
        .crm-field > span { color: #495057; text-align: right; font-size: 13px; font-weight: 600; }
        .crm-field > div, .crm-field > span + span { min-width: 0; }
        .crm-input { width: 100%; min-width: 0; border: 0; border-bottom: 1px solid #e9ecef; border-radius: 0; outline: 0; padding: 10px 0; background: transparent; color: #212529; font: 15px ${pageFont}; box-sizing: border-box; }
        .crm-inline-input { display: flex; align-items: center; gap: 7px; min-width: 0; }
        .crm-salesperson { display: flex; align-items: center; gap: 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; }
        .crm-salesperson span, .crm-feed-avatar { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%; color: #fff; font-size: 11px; font-weight: 700; }
        .crm-stars { display: flex; gap: 2px; }
        .crm-stars button { border: 0; padding: 0; background: transparent; cursor: pointer; }
        .crm-tags { display: flex; flex-wrap: wrap; gap: 3px; }
        .crm-tags span { padding: 4px 9px; border: 1px solid #cce3e3; border-radius: 10px; background: #eef6f3; color: #017e84; font-size: 12px; font-weight: 600; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        .crm-property-button { display: inline-flex; align-items: center; gap: 5px; margin: 16px 0 0 0; border: 0; background: transparent; color: #714b67; font: 14px ${pageFont}; cursor: pointer; }
        .crm-properties-panel { margin: 16px 0 0 0; padding: 0; border: none; background: transparent; }
        .crm-property-row { display: grid; grid-template-columns: 140px 105px minmax(0, 1fr) 30px; gap: 14px; align-items: center; min-height: 58px; }
        .crm-property-row:last-child { margin-bottom: 0; }
        .crm-property-row input, .crm-property-row select { width: 100%; min-width: 0; border: 0; border-bottom: 1px solid #e9ecef; outline: 0; padding: 10px 0; background: transparent; color: #212529; font: 15px ${pageFont}; }
        .crm-property-row button { display: inline-flex; align-items: center; justify-content: center; border: 0; background: transparent; color: #dc3545; cursor: pointer; }
        .crm-tabs { display: flex; padding: 0 36px; border-top: 1px solid #dee2e6; border-bottom: 1px solid #dee2e6; }
        .crm-tabs button { border: 0; border-bottom: 3px solid transparent; padding: 16px 18px 12px; background: transparent; color: #6c757d; font: 14px ${pageFont}; cursor: pointer; }
        .crm-tabs button.active { border-bottom-color: #008784; color: #212529; font-weight: 700; background: #f8fffe; }
        .crm-tab-content { min-height: 260px; padding: 24px 36px 32px; }
        .crm-request-fields { max-width: 100%; }
        .crm-request-fields h2 { margin: 0 0 14px; padding-bottom: 10px; border-bottom: 1px solid #e9ecef; font-size: 13px; text-transform: uppercase; }
        .crm-request-fields label { display: grid; grid-template-columns: 140px 1fr; gap: 16px; align-items: center; min-height: 54px; }
        .crm-request-fields label span { color: #495057; text-align: right; font-size: 13px; }
        .crm-request-fields input { width: 100%; border: 0; border-bottom: 1px solid #e9ecef; outline: 0; padding: 10px 0; font: 15px ${pageFont}; }
        .crm-notes { width: 100%; min-height: 210px; resize: vertical; border: 1px solid #dee2e6; padding: 14px; font: 15px ${pageFont}; box-sizing: border-box; }
        .crm-extra-fields { max-width: 420px; }
        .crm-extra-fields .crm-field > span + span { font-size: 11px; }
        .crm-extra-information { display: grid; grid-template-columns: 1fr 1fr; gap: 28px 42px; }
        .crm-extra-column h2 { margin: 0 0 8px; padding-bottom: 8px; border-bottom: 1px solid #e9ecef; color: #343a40; font-size: 13px; text-transform: uppercase; }
        .crm-address-grid { display: grid; grid-template-columns: 1.3fr 1fr .8fr; gap: 12px; margin: -2px 0 4px 154px; }
        .crm-address-grid + .crm-field { margin-top: 2px; }
        .crm-chatter { margin-top: 32px; overflow: hidden; }
        .crm-chatter-toolbar { justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid #e9ecef; }
        .crm-chatter-toolbar > div:first-child { display: flex; gap: 3px; }
        .crm-chatter-toolbar button { display: inline-flex; align-items: center; gap: 5px; border: 1px solid transparent; padding: 8px 11px; background: transparent; color: #495057; font-size: 13px; }
        .crm-chatter-toolbar button.active { border-color: #dee2e6; background: #212529; color: #fff; }
        .crm-chatter-meta { gap: 12px; color: #6c757d; font-size: 13px; }
        .crm-chatter-meta span { display: inline-flex; align-items: center; gap: 3px; }
        .crm-chatter-meta button { border: 1px solid #dee2e6; background: #fff; }
        .crm-composer { padding: 20px; border-bottom: 1px solid #e9ecef; background: #fcfcfc; }
        .crm-composer textarea { width: 100%; min-height: 120px; resize: vertical; border: 1px solid #dee2e6; padding: 12px; font: 15px ${pageFont}; box-sizing: border-box; }
        .crm-composer > div { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
        .crm-composer button { padding: 7px 13px; border: 1px solid #dee2e6; background: #fff; font-size: 13px; }
        .crm-composer button.primary { border-color: #008784; background: #008784; color: #fff; }
        .crm-composer button:disabled { opacity: .5; cursor: default; }
        .crm-feed { padding: 12px 20px 24px; }
        .crm-feed article { display: flex; gap: 14px; padding: 20px 0; border-bottom: 1px solid #f1f3f5; }
        .crm-feed article:last-child { border-bottom: 0; }
        .crm-feed-avatar { background: #557044; }
        .crm-feed article > div { min-width: 0; flex: 1; }
        .crm-feed article header { display: flex; align-items: baseline; gap: 6px; }
        .crm-feed article header strong { font-size: 13px; }
        .crm-feed article header small { color: #6c757d; font-size: 12px; }
        .crm-feed article p { margin: 7px 0 0; color: #343a40; font-size: 15px; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
        .crm-menu-backdrop { position: fixed; inset: 0; z-index: 40; }
        .crm-action-menu { position: absolute; right: 0; top: calc(100% + 6px); z-index: 50; min-width: 232px; padding: 6px; border: 1px solid #dee2e6; background: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.14); border-radius: 6px; }
        .crm-action-menu button { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px; border: 0; background: transparent; text-align: left; font: 13.5px ${pageFont}; line-height: 1.35; color: #343a40; cursor: pointer; border-radius: 4px; white-space: nowrap; }
        .crm-action-menu button:hover { background: #f1f3f5; color: #212529; }
        .crm-action-menu button:active { background: #e9ecef; }
        @media (max-width: 860px) { .crm-detail-content { width: 92%; max-width: 710px; } .crm-stage-bar { min-width: 420px; } .crm-header-bottom { overflow-x: auto; } }
        @media (max-width: 860px) { .crm-extra-information { grid-template-columns: 1fr; gap: 18px; } }
        @media (max-width: 620px) { .crm-header-top { align-items: flex-start; flex-direction: column; } .crm-header-tools { width: 100%; justify-content: flex-end; } .crm-form-grid { grid-template-columns: 1fr; gap: 4px; } .crm-smart-buttons { justify-content: flex-start; overflow-x: auto; } .crm-chatter-toolbar { align-items: flex-start; flex-direction: column; } .crm-properties-panel { margin-left: 0; } .crm-property-button { margin-left: 0; } .crm-address-grid { margin-left: 0; } .crm-property-row { grid-template-columns: 1fr 90px 1fr 28px; gap: 4px; } }
      `}</style>
    </div>
  );
};

export default CrmV2OpportunityFormView;
